import { useState, useEffect } from "react";
import { T, UI, FONT } from "../constants/tokens";
import { supabase } from "../lib/supabase";
import logo256 from "../assets/suricata-logo-256.png";
import Icon from "./Icon";

export const PROFILE_KEY = "investorProfile";

// ─── Display-adaptation questionnaire ────────────────────────────────────────
// LEGAL: this is NOT a risk-profiling / advisory questionnaire. It only changes
// the ORDER and EMPHASIS of information on screen — never the content, never a
// recommendation. Each answer contributes one point to a display style; the
// majority style wins (ties → balanced).
//
// Styles: conservative (shield) / balanced (scale) / growth (rocket)

const QUESTIONS = [
  {
    key: "goal",
    q: "מה המטרה המרכזית של ההשקעות שלך?",
    options: [
      { label: "בניית הון לטווח ארוך", style: "balanced" },
      { label: "הכנסה שוטפת",          style: "conservative" },
      { label: "צמיחה מהירה",          style: "growth" },
    ],
  },
  {
    key: "drawdown",
    q: "איך תגיב אם התיק יירד 15% בחודש?",
    options: [
      { label: "אצמצם חשיפה",     style: "conservative" },
      { label: "לא אשנה דבר",     style: "balanced" },
      { label: "אגדיל פוזיציות",  style: "growth" },
    ],
  },
  {
    key: "horizon",
    q: "מה אופק ההשקעה שלך?",
    options: [
      { label: "עד 3 שנים",   style: "conservative" },
      { label: "3–10 שנים",   style: "balanced" },
      { label: "מעל 10 שנים", style: "growth" },
    ],
  },
  {
    key: "exposure",
    q: "איזה חלק מהחיסכון שלך מושקע בשוק ההון?",
    options: [
      { label: "פחות מרבע", style: "conservative" },
      { label: "רבע עד חצי", style: "balanced" },
      { label: "מעל חצי",    style: "growth" },
    ],
  },
  {
    key: "monitoring",
    q: "כמה זמן תרצה להקדיש למעקב?",
    options: [
      { label: "כמה דקות ביום",     style: "growth" },
      { label: "כמה פעמים בשבוע",   style: "balanced" },
      { label: "מינימום הכרחי",     style: "conservative" },
    ],
  },
];
export const TOTAL = QUESTIONS.length;

const STYLE_META = {
  conservative: { name: "שמרני", icon: "shield", blurb: "נדגיש קודם ריכוזיות, דיבידנד ואירועים, עם הסברים מפורטים." },
  balanced:     { name: "מאוזן", icon: "scale",  blurb: "תצוגה מאוזנת בין סקירת התיק, מודיעין ותנועות." },
  growth:       { name: "צמיחה", icon: "rocket", blurb: "נדגיש קודם תנועות בולטות, דוחות ומודיעין, בתצוגה קומפקטית." },
};

function deriveStyle(answers) {
  const tally = { conservative: 0, balanced: 0, growth: 0 };
  for (const q of QUESTIONS) {
    const v = answers[q.key];
    if (v && tally[v] != null) tally[v] += 1;
  }
  const max = Math.max(tally.conservative, tally.balanced, tally.growth);
  // ties resolve to balanced
  if (tally.balanced === max) return "balanced";
  if (tally.growth === max) return "growth";
  return "conservative";
}

// display_style → profile_type (table CHECK allows conservative/balanced/aggressive)
const STYLE_TO_PROFILE = { conservative: "conservative", balanced: "balanced", growth: "aggressive" };

// ─── Optional name capture ───────────────────────────────────────────────────
// The name is stored ONLY in Supabase auth user_metadata.full_name — no new
// column and no migration. utils/userName.getFirstName() reads exactly that
// field, so the greeting starts working the moment this is saved.
// First + last are joined into one full_name separated by a single space.
// Both fields are optional: an empty form saves nothing and blocks nothing.
// Deliberately no phone number, here or anywhere in this flow.
const MAX_NAME_LEN = 40;

// Trim, collapse inner whitespace, cap the length.
const cleanNamePart = (s) =>
  typeof s === "string" ? s.trim().replace(/\s+/g, " ").slice(0, MAX_NAME_LEN) : "";

const joinFullName = (first, last) =>
  [cleanNamePart(first), cleanNamePart(last)].filter(Boolean).join(" ");

function NameScreen({ first, last, onFirst, onLast, onContinue, onSkip, eyebrow = "שאלון התאמה" }) {
  const inputStyle = {
    width: "100%",
    background: UI.surface.raised,
    border: `1px solid ${UI.line.hairline}`,
    borderRadius: 12,
    padding: "16px 18px",
    minHeight: 56,
    fontSize: 16,            // 16px keeps iOS from zooming on focus
    fontWeight: 600,
    color: UI.text.primary,
    fontFamily: "inherit",
    outline: "none",
    boxSizing: "border-box",
    textAlign: "right",
  };
  const labelStyle = { display: "block", fontSize: 13, color: UI.text.muted, fontWeight: 600, marginBottom: 6 };

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, position: "relative" }}>
      <div style={{ padding: "44px 24px 26px", display: "flex", flexDirection: "column", flex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: UI.text.muted }}>{eyebrow}</span>
        </div>
        <div style={{ fontSize: 20, fontWeight: 800, color: UI.text.primary, lineHeight: 1.45, marginBottom: 8, fontFamily: FONT.heading }}>
          איך לפנות אליך?
        </div>
        <div style={{ fontSize: 13, color: UI.text.muted, lineHeight: 1.7, marginBottom: 24 }}>
          שני השדות אופציונליים · אפשר לדלג ולהמשיך
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <label htmlFor="si-first-name" style={labelStyle}>שם פרטי</label>
            <input
              id="si-first-name"
              dir="auto"
              value={first}
              onChange={e => onFirst(e.target.value)}
              maxLength={MAX_NAME_LEN}
              autoComplete="given-name"
              style={inputStyle}
            />
          </div>
          <div>
            <label htmlFor="si-last-name" style={labelStyle}>שם משפחה</label>
            <input
              id="si-last-name"
              dir="auto"
              value={last}
              onChange={e => onLast(e.target.value)}
              maxLength={MAX_NAME_LEN}
              autoComplete="family-name"
              style={inputStyle}
            />
          </div>
          <div style={{ fontSize: 12, color: UI.text.muted, lineHeight: 1.6 }}>
            לשימוש בברכה האישית באפליקציה בלבד
          </div>
        </div>

        <div style={{ flex: 1, minHeight: 24 }}/>
        <button onClick={onContinue} className="si-btn-gold"
          style={{ background: "transparent", border: `1px solid ${UI.accent.base}`, borderRadius: T.radius.md, padding: "16px 0", fontSize: 16, fontWeight: 700, color: UI.accent.base, cursor: "pointer", width: "100%", minHeight: 48 }}>
          המשך ←
        </button>
        <div style={{ textAlign: "center" }}>
          <button onClick={onSkip} style={{ background: "none", border: "none", fontSize: 13, color: UI.text.muted, cursor: "pointer", padding: "14px 22px" }}>
            דלג לעת עתה
          </button>
        </div>
      </div>
    </div>
  );
}

function ProgressBar({ current }) {
  return (
    <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: UI.fill.subtle }}>
      <div style={{ height: "100%", width: `${(current / TOTAL) * 100}%`, background: UI.series[1], transition: "width .35s ease", borderRadius: "0 2px 2px 0" }}/>
    </div>
  );
}

function IntroScreen({ onStart, onSkip }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flex: 1, padding: "40px 26px", textAlign: "center" }}>
      <img src={logo256} alt="Suricata" style={{ width: 72, height: 72, borderRadius: "22%", background: UI.logoPlate, objectFit: "cover", marginBottom: 32 }}/>
      <div style={{ fontSize: 25, fontWeight: 800, color: UI.text.primary, marginBottom: 12, fontFamily: FONT.heading }}>
        שאלון התאמה אישית
      </div>
      <div style={{ fontSize: 15, color: UI.text.secondary, lineHeight: 1.7, marginBottom: 10, maxWidth: 340 }}>
        מתאים את אופן הצגת המידע בלבד
      </div>
      <div style={{ fontSize: 13, color: UI.text.muted, marginBottom: 44 }}>5 שאלות · פחות מדקה</div>
      <button onClick={onStart} className="si-btn-gold" style={{ background: "transparent", border: `1px solid ${UI.accent.base}`, borderRadius: T.radius.md, padding: "16px 0", fontSize: 16, fontWeight: 700, color: UI.accent.base, cursor: "pointer", width: "100%", maxWidth: 320, minHeight: 48 }}>
        בואו נתחיל ←
      </button>
      <button onClick={onSkip} style={{ background: "none", border: "none", fontSize: 13, color: UI.text.muted, cursor: "pointer", padding: "14px 22px", marginTop: 6 }}>
        דלג לעת עתה
      </button>
    </div>
  );
}

function QuestionScreen({ qIndex, q, onAnswer, onBack, onSkip }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, position: "relative" }}>
      <ProgressBar current={qIndex}/>
      <div style={{ padding: "44px 24px 26px", display: "flex", flexDirection: "column", flex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: UI.text.muted }}>שאלון התאמה</span>
          <span style={{ fontSize: 12, color: UI.text.muted, fontWeight: 600 }}><span dir="ltr">{qIndex} / {TOTAL}</span></span>
        </div>
        <div style={{ fontSize: 20, fontWeight: 800, color: UI.text.primary, lineHeight: 1.45, marginBottom: 28, fontFamily: FONT.heading }}>
          {q.q}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {q.options.map((opt, i) => (
            <button key={i} onClick={() => onAnswer(opt.style)}
              style={{ display: "flex", alignItems: "center", padding: "16px 18px", minHeight: 56, background: UI.surface.raised, border: `1px solid ${UI.line.hairline}`, borderRadius: T.radius.xl, boxShadow: UI.shadow.card, cursor: "pointer", textAlign: "right", width: "100%" }}>
              <span style={{ fontSize: 15, fontWeight: 600, color: UI.text.primary }}>{opt.label}</span>
            </button>
          ))}
        </div>
        <div style={{ flex: 1 }}/>
        <div style={{ textAlign: "center", display: "flex", justifyContent: "center", gap: 16 }}>
          {qIndex > 1 && (
            <button onClick={onBack} style={{ background: "none", border: "none", fontSize: 13, color: UI.text.muted, cursor: "pointer", padding: "10px 16px" }}>← הקודם</button>
          )}
          <button onClick={onSkip} style={{ background: "none", border: "none", fontSize: 13, color: UI.text.muted, cursor: "pointer", padding: "10px 16px" }}>דלג לעת עתה</button>
        </div>
      </div>
    </div>
  );
}

function ResultScreen({ style, onComplete }) {
  const meta = STYLE_META[style] || STYLE_META.balanced;
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flex: 1, textAlign: "center", padding: "40px 26px" }}>
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 20 }}><Icon name={meta.icon} size={56} color={UI.text.muted}/></div>
      <div style={{ fontSize: 16, color: UI.text.secondary, marginBottom: 6 }}>סגנון התצוגה שלך</div>
      <div style={{ fontSize: 26, fontWeight: 800, color: UI.text.primary, marginBottom: 14, fontFamily: FONT.heading }}>{meta.name}</div>
      <div style={{ fontSize: 14, color: UI.text.secondary, lineHeight: 1.7, marginBottom: 12, maxWidth: 360 }}>{meta.blurb}</div>
      <div style={{ fontSize: 13, color: UI.text.muted, lineHeight: 1.6, marginBottom: 40, maxWidth: 360 }}>
        סוריקטה תתאים את סדר המידע והדגשים. אפשר לשנות בכל עת בהגדרות.
      </div>
      <button onClick={onComplete} className="si-btn-gold" style={{ background: "transparent", border: `1px solid ${UI.accent.base}`, borderRadius: T.radius.md, padding: "16px 0", fontSize: 16, fontWeight: 700, color: UI.accent.base, cursor: "pointer", width: "100%", maxWidth: 320, minHeight: 48 }}>
        כניסה לאפליקציה ←
      </button>
    </div>
  );
}

// Step map: 0=intro · 1=name · 2..TOTAL+1=questions · TOTAL+2=result.
// Question N lives at step N+1, so QuestionScreen still receives 1..TOTAL and
// the progress bar / "N / TOTAL" counter need no change.
const NAME_STEP   = 1;
const FIRST_Q     = 2;
const LAST_Q      = TOTAL + 1;
const RESULT_STEP = TOTAL + 2;

export default function OnboardingFlow({ onComplete }) {
  const [step, setStep]       = useState(0);
  const [answers, setAnswers] = useState({});
  const [style, setStyle]     = useState(null);
  const [nameFirst, setNameFirst] = useState("");
  const [nameLast,  setNameLast]  = useState("");

  // Retaking the questionnaire shows whatever name is already stored.
  useEffect(() => {
    if (!supabase) return;
    let alive = true;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const stored = session?.user?.user_metadata?.full_name;
        if (!alive || typeof stored !== "string" || !stored.trim()) return;
        const parts = stored.trim().split(/\s+/);
        setNameFirst(parts[0] || "");
        setNameLast(parts.slice(1).join(" "));
      } catch (err) {
        console.error("[OnboardingFlow] could not read stored name:", err?.message);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Persisted to auth user_metadata, never to investor_profiles. An empty form
  // writes nothing at all, so skipping leaves the account exactly as it was.
  const saveName = async (first, last) => {
    if (!supabase) return;
    const full = joinFullName(first, last);
    if (!full) return;
    try {
      const { error } = await supabase.auth.updateUser({ data: { full_name: full } });
      if (error) console.error("[OnboardingFlow] updateUser full_name failed:", error.message);
    } catch (err) {
      console.error("[OnboardingFlow] save name failed:", err?.message);
    }
  };

  const save = async (displayStyle, allAnswers) => {
    try {
      localStorage.setItem(PROFILE_KEY, JSON.stringify({ display_style: displayStyle, ...allAnswers, completedAt: Date.now() }));
    } catch { /* private mode */ }
    if (!supabase) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) return;
      const { error } = await supabase.from("investor_profiles").upsert({
        user_id:               session.user.id,
        display_style:         displayStyle,
        profile_type:          STYLE_TO_PROFILE[displayStyle] || "balanced",
        answers:               allAnswers,
        questionnaire_version: "2",
        completed_at:          new Date().toISOString(),
      }, { onConflict: "user_id" });
      if (error) console.error("[OnboardingFlow] upsert error:", error.code, error.message);
    } catch (err) {
      console.error("[OnboardingFlow] save failed:", err?.message);
    }
  };

  const handleAnswer = (val) => {
    const q = QUESTIONS[step - FIRST_Q];
    const updated = { ...answers, [q.key]: val };
    setAnswers(updated);
    if (step === LAST_Q) {
      const s = deriveStyle(updated);
      setStyle(s);
      save(s, updated);
      setStep(RESULT_STEP);
    } else {
      setStep(s => s + 1);
    }
  };

  // Skip: persist "balanced" as a neutral default so the questionnaire doesn't
  // reappear every launch, but the user can re-take from Settings. A name that
  // was already typed is still saved — it is independent of the questionnaire.
  const handleSkip = () => {
    saveName(nameFirst, nameLast);
    save("balanced", answers);
    onComplete?.();
  };

  // Name is persisted when leaving the name screen, so it survives an
  // abandoned questionnaire.
  const leaveNameScreen = () => {
    saveName(nameFirst, nameLast);
    setStep(FIRST_Q);
  };

  const q = step >= FIRST_Q && step <= LAST_Q ? QUESTIONS[step - FIRST_Q] : null;

  return (
    <div dir="rtl" style={{ position: "fixed", inset: 0, zIndex: 9999, background: UI.surface.page, display: "flex", alignItems: "stretch", justifyContent: "center", overflowY: "auto" }}>
      <div style={{ width: "100%", maxWidth: 480, display: "flex", flexDirection: "column", minHeight: "100dvh" }}>
        {step === 0 && <IntroScreen onStart={() => setStep(NAME_STEP)} onSkip={handleSkip}/>}
        {step === NAME_STEP && (
          <NameScreen
            first={nameFirst}
            last={nameLast}
            onFirst={setNameFirst}
            onLast={setNameLast}
            onContinue={leaveNameScreen}
            onSkip={handleSkip}
          />
        )}
        {q && <QuestionScreen qIndex={step - 1} q={q} onAnswer={handleAnswer} onBack={() => setStep(s => s - 1)} onSkip={handleSkip}/>}
        {step === RESULT_STEP && <ResultScreen style={style} onComplete={onComplete}/>}
      </div>
    </div>
  );
}

// ─── The name screen on its own ─────────────────────────────────────────────
// While the questionnaire is hidden (INVESTOR_PROFILE_ENABLED, DECISIONS.md
// 27/09/2026) a new user still gets the one screen that was never about
// investing: an optional name for the greeting. Nothing else is asked.
//
// Shown once. Continue or skip both write `name_prompted: true` to auth
// user_metadata (no table, no migration), so the screen does not return on the
// next sign-in; a name, when given, is written to full_name as before. Nothing
// here touches investor_profiles.
export function NameOnlyFlow({ onDone }) {
  const [first, setFirst] = useState("");
  const [last,  setLast]  = useState("");
  const [busy,  setBusy]  = useState(false);

  const finish = async (withName) => {
    if (busy) return;
    setBusy(true);
    const full = withName ? joinFullName(first, last) : "";
    if (supabase) {
      try {
        const { error } = await supabase.auth.updateUser({ data: full ? { full_name: full, name_prompted: true } : { name_prompted: true } });
        if (error) console.error("[NameOnlyFlow] updateUser failed:", error.message);
      } catch (err) {
        console.error("[NameOnlyFlow] save failed:", err?.message);
      }
    }
    onDone?.();
  };

  return (
    <div dir="rtl" style={{ position: "fixed", inset: 0, zIndex: 9999, background: UI.surface.page, display: "flex", alignItems: "stretch", justifyContent: "center", overflowY: "auto" }}>
      <div style={{ width: "100%", maxWidth: 480, display: "flex", flexDirection: "column", minHeight: "100dvh" }}>
        <NameScreen
          eyebrow="ברוכים הבאים"
          first={first}
          last={last}
          onFirst={setFirst}
          onLast={setLast}
          onContinue={() => finish(true)}
          onSkip={() => finish(false)}
        />
      </div>
    </div>
  );
}
